/**
 * HomePage - Enhanced Recreation of Base44 Dashboard Homepage
 * Integrates all Phase 2 dashboard components with proper data flow
 * 
 * @description Professional dashboard interface with real Base44 functionality,
 * replacing the basic implementation with comprehensive cloud management features
 */

// Phase 3: Import new cloud provider hook
import { useCloudProvider } from '@/hooks/useCloudProvider';
import type { CloudProvider } from '@/types/cloudProvider';

// Import our Phase 2 dashboard components
import { OverviewCards } from '@/components/dashboard/OverviewCards';
import { ProviderSwitcher } from '@/components/dashboard/ProviderSwitcher';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { CostOverview } from '@/components/dashboard/CostOverview';
import { ResourcesList } from '@/components/dashboard/ResourcesList';

// Mock data for dashboard functionality (Phase 2 simplified)
const mockCostData = {
  current: 1247.89,
  previous: 1180.50,
  budget: 1500.00,
  currency: 'USD'
};

const mockResourceCounts = {
  aws: 23,
  gcp: 15,
  azure: 9
};

const mockResources = [
  {
    id: 'aws-ec2-001',
    name: 'web-server-prod-01',
    type: 'compute' as const,
    provider: 'aws' as const,
    status: 'running' as const,
    health: 'healthy' as const,
    region: 'us-east-1',
    cost: 89.50
  },
  {
    id: 'aws-rds-001',
    name: 'user-database-main',
    type: 'database' as const,
    provider: 'aws' as const,
    status: 'running' as const,
    health: 'healthy' as const,
    region: 'us-east-1',
    cost: 156.80
  },
  {
    id: 'gcp-compute-001',
    name: 'analytics-worker-01',
    type: 'compute' as const,
    provider: 'gcp' as const,
    status: 'running' as const,
    health: 'warning' as const,
    region: 'us-central1',
    cost: 67.30
  },
  {
    id: 'azure-vm-001',
    name: 'backup-processor',
    type: 'compute' as const,
    provider: 'azure' as const,
    status: 'stopped' as const,
    health: 'healthy' as const,
    region: 'eastus',
    cost: 45.20
  },
  {
    id: 'aws-s3-001',
    name: 'media-storage-bucket',
    type: 'storage' as const,
    provider: 'aws' as const,
    status: 'running' as const,
    health: 'healthy' as const,
    region: 'us-west-2',
    cost: 23.40
  }
];

export const HomePage = () => {
  // Phase 3: Migration from useState to Zustand store
  // Backward compatible migration - same interface as useState
  const [activeProvider, setActiveProvider] = useCloudProvider();

  // Handle provider change - Phase 3: Updated for CloudProvider type
  const handleProviderChange = (provider: CloudProvider) => {
    setActiveProvider(provider);
  };

  // Handle quick actions
  const handleQuickAction = async (action: string) => {
    // In a real implementation, this would trigger actual cloud operations
    console.log(`Executed ${action} for ${activeProvider}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header Section */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Cloud Infrastructure Dashboard
              </h1>
              <p className="text-muted-foreground mt-2">
                Monitor and manage your multi-cloud resources in real-time
              </p>
            </div>
            
            {/* Provider Switcher */}
            <ProviderSwitcher 
              activeProvider={activeProvider}
              onProviderChange={handleProviderChange}
              resourceCounts={mockResourceCounts}
            />
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="space-y-8">
          
          {/* Overview Metrics */}
          <section>
            <OverviewCards 
              resources={mockResources}
              activeProvider={activeProvider}
            />
          </section>

          {/* Quick Actions & Cost Overview Row */}
          <section className="grid gap-6 lg:grid-cols-2">
            <QuickActions 
              activeProvider={activeProvider}
              onActionClick={handleQuickAction}
            />
            
            <CostOverview 
              costData={mockCostData}
              activeProvider={activeProvider}
            />
          </section>

          {/* Resources List */}
          <section>
            <ResourcesList 
              resources={mockResources}
              activeProvider={activeProvider}
            />
          </section>

          {/* Recent Activity Section (Placeholder for Phase 3) */}
          <section className="rounded-lg border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                Activity monitoring will be implemented in Phase 3
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
